import { Component, ErrorInfo, ReactNode } from 'react';
import { Button, Result } from 'antd';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  // Only attach global listeners in development
  public componentDidMount() {
    if (process.env.NODE_ENV === 'development') {
      globalThis.addEventListener('error', this.handleGlobalError);
      globalThis.addEventListener('unhandledrejection', this.handleUnhandledRejection);
    }
  }

  public componentWillUnmount() {
    if (process.env.NODE_ENV === 'development') {
      globalThis.removeEventListener('error', this.handleGlobalError);
      globalThis.removeEventListener('unhandledrejection', this.handleUnhandledRejection);
    }
  }

  // React errors in components
  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Uncaught React error:', error, errorInfo);
    }
  }

  // Global JS errors (only in dev)
  private readonly handleGlobalError = (event: ErrorEvent) => {
    this.setState({
      hasError: true,
      error:
        event.error instanceof Error
          ? event.error
          : new Error(event.message || 'Une erreur inattendue est survenue.'),
    });

    if (process.env.NODE_ENV === 'development') {
      console.error('Global JS error:', event.error ?? event.message, event);
    }
  };

  // Unhandled Promise rejections (only in dev)
  private readonly handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    const error =
      reason instanceof Error
        ? reason
        : new Error(
            typeof reason === 'string' ? reason : 'Une erreur asynchrone inattendue est survenue.',
          );

    this.setState({ hasError: true, error });

    if (process.env.NODE_ENV === 'development') {
      console.error('Unhandled promise rejection:', reason, event);
    }
  };

  // Reset state and redirect home
  private readonly handleReset = () => {
    this.setState({ hasError: false, error: null });
    globalThis.location.assign('/');
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div
          data-testid="error-boundary-container"
          style={{
            height: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            background: '#f0f2f5',
          }}
        >
          <Result
            status="error"
            title="Une erreur est survenue"
            subTitle="Désolé, une erreur inattendue s'est produite. Veuillez réessayer ou contacter le support."
            extra={[
              <Button type="primary" key="home" onClick={this.handleReset}>
                Retour à l'accueil
              </Button>,
              <Button key="retry" onClick={() => globalThis.location.reload()}>
                Actualiser la page
              </Button>,
            ]}
          />
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
